import React, { useEffect, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api, publicFileUrl } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Trophy, Rocket, Shuffle } from "lucide-react";
import GironeBadge from "@/components/GironeBadge";
import { Link, useNavigate } from "react-router-dom";

const SHIELD_PLACEHOLDER = "https://images.unsplash.com/photo-1577214322326-5b31d9d886b0?crop=entropy&cs=srgb&fm=jpg&w=200&q=80";

function CoppaShell({ title, icon: Icon, accent, children }) {
  return (
    <div className="App">
      <Header />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 space-y-6">
        <div className="fade-up flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: `${accent}22`, color: accent }}>
            <Icon className="w-6 h-6" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">Coppa FIFE</div>
            <h1 className="display font-black text-4xl md:text-5xl">{title}</h1>
          </div>
        </div>
        {children}
      </div>
      <Footer />
    </div>
  );
}

function itDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso + "T12:00:00");
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "2-digit" });
  } catch { return iso; }
}

function TwoLegMatchRow({ m, accent }) {
  const played = m.status === "played";
  const homeWin = played && m.winner_id === m.home_id;
  const awayWin = played && m.winner_id === m.away_id;
  const HS = accent || "#60a5fa";
  return (
    <div className="border border-slate-200 rounded-xl p-3 hover:border-[var(--brand)]/40 transition"
         data-testid={`twoleg-match-${m.id}`}>
      <div className="grid grid-cols-[auto_1fr_auto_auto_auto_1fr_auto] items-center gap-2">
        <span className="text-[10px] font-black px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">#{m.seed_home}</span>
        <div className="flex items-center gap-2 min-w-0">
          <GironeBadge girone={m.home_girone} size={18} />
          <span className={`text-sm truncate ${homeWin ? "font-black text-slate-900" : "text-slate-600"}`}>{m.home_name}</span>
          {homeWin && <Trophy className="w-3.5 h-3.5 shrink-0" style={{ color: HS }} />}
        </div>
        <span className="display font-black text-lg tabular-nums text-slate-900">{played ? m.agg_home : "—"}</span>
        <span className="text-slate-300">–</span>
        <span className="display font-black text-lg tabular-nums text-slate-900">{played ? m.agg_away : "—"}</span>
        <div className="flex items-center gap-2 justify-end min-w-0">
          {awayWin && <Trophy className="w-3.5 h-3.5 shrink-0" style={{ color: HS }} />}
          <span className={`text-sm truncate text-right ${awayWin ? "font-black text-slate-900" : "text-slate-600"}`}>{m.away_name}</span>
          <GironeBadge girone={m.away_girone} size={18} />
        </div>
        <span className="text-[10px] font-black px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">#{m.seed_away}</span>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2 text-[11px] text-slate-600">
        <div className="rounded bg-slate-50 border border-slate-100 px-2 py-1 flex items-center justify-between" data-testid={`leg1-${m.id}`}>
          <span className="uppercase tracking-widest text-[10px] text-slate-500">Andata {itDate(m.date_leg1)}</span>
          <span className="tabular-nums font-bold text-slate-800">{m.leg1_home ?? "-"}–{m.leg1_away ?? "-"}</span>
        </div>
        <div className="rounded bg-slate-50 border border-slate-100 px-2 py-1 flex items-center justify-between" data-testid={`leg2-${m.id}`}>
          <span className="uppercase tracking-widest text-[10px] text-slate-500">Ritorno {itDate(m.date_leg2)}</span>
          <span className="tabular-nums font-bold text-slate-800">{m.leg2_home ?? "-"}–{m.leg2_away ?? "-"}</span>
        </div>
      </div>
    </div>
  );
}

function CupStagePlaceholder({ title }) {
  return (
    <div className="border border-dashed border-slate-200 rounded-2xl p-8 text-center">
      <div className="text-[10px] uppercase tracking-widest text-slate-400">{title}</div>
      <div className="display font-bold text-slate-500 mt-1">Ancora da disputare</div>
      <div className="text-slate-400 text-xs mt-1">Il sorteggio si genererà quando la fase precedente sarà completata.</div>
    </div>
  );
}

/**
 * Bracket panoramico: 4 colonne (Ottavi/Quarti/Semi/Finale) sempre visibili.
 * Le colonne con dati mostrano nomi squadra + data + punteggio aggregato.
 * Le colonne vuote mostrano placeholder "—".
 */
function BracketOverview({ ottavi = [], quarti = [], semi = [], finale = null, accent = "#60a5fa", competition = "Champions" }) {
  const rounds = [
    { key: "ottavi",  label: "Ottavi",     count: 8, items: ottavi },
    { key: "quarti",  label: "Quarti",     count: 4, items: quarti },
    { key: "semi",    label: "Semifinali", count: 2, items: semi },
    { key: "finale",  label: "Finale",     count: 1, items: finale ? [finale] : [] },
  ];
  return (
    <div className="card-surface rounded-2xl p-4 md:p-6" data-testid={`bracket-overview-${competition.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Tabellone</div>
          <div className="display font-black text-lg">Panoramica {competition}</div>
        </div>
        <div className="hidden sm:flex items-center gap-3 text-[10px] uppercase tracking-widest text-slate-500">
          <span>Ottavi</span><span>→</span><span>Quarti</span><span>→</span><span>Semi</span><span>→</span><span>Finale</span>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2 md:gap-3 overflow-x-auto">
        {rounds.map((r) => (
          <div key={r.key} className="flex flex-col gap-2 md:gap-3" data-testid={`bracket-col-${r.key}`}>
            <div className="text-center text-[10px] font-black uppercase tracking-widest text-slate-500 py-1 rounded"
                 style={{ background: `${accent}18`, color: accent }}>{r.label}</div>
            {Array.from({ length: r.count }).map((_, i) => {
              const m = r.items[i];
              return <BracketCell key={i} match={m} accent={accent} slotNumber={i + 1} round={r.key} />;
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function BracketCell({ match, accent, slotNumber, round }) {
  if (!match) {
    return (
      <div className="rounded border border-dashed border-slate-200 bg-slate-50/40 p-2 min-h-[56px] flex items-center justify-center text-[10px] text-slate-400 uppercase tracking-widest"
           data-testid={`bracket-cell-${round}-empty-${slotNumber}`}>
        —
      </div>
    );
  }
  const played = match.status === "played";
  const hs = played ? match.agg_home : "-";
  const as_ = played ? match.agg_away : "-";
  const homeWin = played && match.winner_id === match.home_id;
  const awayWin = played && match.winner_id === match.away_id;
  const dates = [match.date_leg1, match.date_leg2].filter(Boolean).map(itDate);
  const shortName = (n) => (n || "").length > 12 ? (n || "").slice(0, 11) + "…" : (n || "");
  return (
    <div className="rounded border border-slate-200 hover:border-[var(--brand)]/40 transition p-2 text-[11px]" data-testid={`bracket-cell-${round}-${slotNumber}`}>
      <div className="flex items-center justify-between gap-1">
        <span className={`truncate ${homeWin ? "font-black text-slate-900" : "text-slate-600"}`}>{shortName(match.home_name)}</span>
        <span className="font-black tabular-nums text-slate-800 shrink-0">{hs}</span>
      </div>
      <div className="flex items-center justify-between gap-1 mt-0.5">
        <span className={`truncate ${awayWin ? "font-black text-slate-900" : "text-slate-600"}`}>{shortName(match.away_name)}</span>
        <span className="font-black tabular-nums text-slate-800 shrink-0">{as_}</span>
      </div>
      {dates.length > 0 && (
        <div className="mt-1 text-[9px] text-slate-500 uppercase tracking-widest flex gap-1">
          {dates.map((d, i) => <span key={i} className="truncate">{d}</span>)}
        </div>
      )}
    </div>
  );
}

export function CoppaFaseGirone() {
  const [standings, setStandings] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [config, setConfig] = useState({ schedule: [], thresholds: [] });
  useEffect(() => {
    api.get("/public/coppa/maxi/standings").then(({ data }) => setStandings(data || [])).catch(() => {});
    api.get("/public/coppa/maxi/matches").then(({ data }) => setRounds(data || [])).catch(() => {});
    api.get("/public/coppa/maxi/config").then(({ data }) => setConfig(data || { schedule: [], thresholds: [] })).catch(() => {});
  }, []);
  return (
    <CoppaShell title="Fase a Girone" icon={Trophy} accent="var(--brand)">
      <div className="card-surface rounded-2xl p-5 md:p-6" data-testid="fase-girone-formato">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">Formato</div>
        <p className="text-slate-700 text-sm md:text-base mt-3 leading-relaxed">
          Al termine delle 8 partite di Girone le prime <b>8 squadre classificate</b> entrano come <b>teste di serie</b> agli
          Ottavi di Champions League, dalla <b>9ª alla 24ª</b> disputeranno i <b>Playoff</b> per entrare in Champions League,
          le <b>8 vincenti</b> entrano nel tabellone della Champions, le <b>8 perdenti</b> saranno invece le <b>teste di serie</b>
          del tabellone di Europa League, in cui si scontreranno con le squadre, dalla <b>25ª alla 32ª</b> classificata,
          del Girone Unico di qualificazione.
        </p>
      </div>
      {standings.length > 0 && <MaxiStandings rows={standings} />}
      {rounds.length > 0 && <MaxiRoundsMatches rounds={rounds} thresholds={config.thresholds || []} />}
    </CoppaShell>
  );
}

function MaxiRoundsMatches({ rounds, thresholds }) {
  const [activeRound, setActiveRound] = useState(rounds[0]?.round || 1);
  const [breakdown, setBreakdown] = useState(null);
  const cur = rounds.find((r) => r.round === activeRound) || rounds[0];
  return (
    <div className="card-surface rounded-2xl overflow-hidden" data-testid="maxi-rounds-matches">
      <div className="px-5 py-3 border-b border-slate-100">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">Calendario Coppa</div>
        <div className="display font-black text-lg">Partite per giornata</div>
      </div>
      <div className="flex flex-wrap gap-1.5 px-3 sm:px-4 py-3 border-b border-slate-100 bg-slate-50 overflow-x-auto no-scrollbar">
        {rounds.map((r) => (
          <button key={r.round} onClick={() => setActiveRound(r.round)}
                  data-testid={`round-tab-${r.round}`}
                  className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold transition-colors
                    ${activeRound === r.round ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-100"}`}>
                  G{r.round}
          </button>
        ))}
      </div>
      {cur && (
        <div className="p-4 md:p-5">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="display font-bold text-lg">Giornata {cur.round} · Coppa</div>
              {cur.campionato_matchday ? (
                <div className="text-xs text-slate-500 mt-0.5">
                  Punteggi presi dalla <b>{cur.campionato_matchday}ª Giornata</b> Campionato FIFE
                </div>
              ) : (
                <div className="text-xs text-amber-600 mt-0.5">
                  ⚠ Nessuna Giornata Campionato associata (config non impostata)
                </div>
              )}
            </div>
          </div>
          <div className="space-y-2">
            {cur.matches.map((m) => (
              <button key={m.id}
                      onClick={() => setBreakdown(m)}
                      className="w-full grid grid-cols-[1fr_auto_1fr] items-center gap-1.5 sm:gap-4 rounded-xl border border-slate-200 px-2.5 sm:px-3 py-2.5 bg-white hover:border-slate-400 hover:shadow-sm transition-all text-left active:scale-[0.99]"
                      data-testid={`maxi-match-${m.id}`}>
                <div className="text-right min-w-0">
                  <div className="font-semibold text-slate-900 text-xs sm:text-sm md:text-base truncate">{m.home_name}</div>
                  {m.home_points != null && <div className="text-[9px] sm:text-[10px] text-slate-500">{m.home_points} pt</div>}
                </div>
                <div className="text-center shrink-0">
                  {m.status === "played" ? (
                    <div className="display font-black text-base sm:text-xl px-2">{m.home_score} - {m.away_score}</div>
                  ) : (
                    <div className="text-[10px] sm:text-xs text-slate-400 px-2 py-1 rounded-full border border-slate-200">vs</div>
                  )}
                </div>
                <div className="text-left min-w-0">
                  <div className="font-semibold text-slate-900 text-xs sm:text-sm md:text-base truncate">{m.away_name}</div>
                  {m.away_points != null && <div className="text-[9px] sm:text-[10px] text-slate-500">{m.away_points} pt</div>}
                </div>
              </button>
            ))}
          </div>
          {thresholds.length > 0 && (
            <details className="mt-4 text-xs text-slate-500">
              <summary className="cursor-pointer text-slate-600 font-semibold">Soglie gol ({thresholds.length})</summary>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {thresholds.map((t, i) => (
                  <span key={i} className="px-2 py-1 rounded-full bg-blue-100 text-blue-800 text-[10px] font-bold">
                    {i + 1}° GOL @ {t}pt
                  </span>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
      <MatchBreakdownModal m={breakdown} onClose={() => setBreakdown(null)} />
    </div>
  );
}

function MatchBreakdownModal({ m, onClose }) {
  if (!m) return null;
  const hm = m.home_midfield;
  const am = m.away_midfield;
  const hasCup = m.cup_midfield_applied && hm && am;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4" onClick={onClose}
         data-testid="coppa-breakdown-modal">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-lg w-full p-4 sm:p-5 max-h-[92vh] overflow-y-auto"
           style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom, 0px))" }}
           onClick={(e) => e.stopPropagation()}>
        {/* Handle-bar mobile per feedback bottom-sheet */}
        <div className="w-10 h-1.5 rounded-full bg-slate-300 mx-auto mb-3 sm:hidden" aria-hidden="true" />
        <div className="flex items-start justify-between mb-4 gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Dettaglio partita</div>
            <div className="display font-black text-base sm:text-lg break-words">{m.home_name} vs {m.away_name}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl shrink-0 w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100">✕</button>
        </div>
        {m.status !== "played" ? (
          <div className="text-slate-500 text-center py-8">Partita non ancora giocata</div>
        ) : (
          <>
            <div className="rounded-xl bg-slate-50 p-4 text-center mb-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Risultato</div>
              <div className="display font-black text-3xl">{m.home_score} - {m.away_score}</div>
              {m.campionato_matchday && (
                <div className="text-xs text-slate-500 mt-1">Basato su {m.campionato_matchday}ª Giornata FIFE</div>
              )}
            </div>
            {hasCup ? (
              <div className="grid grid-cols-2 gap-3">
                <BreakdownSide name={m.home_name} pts={m.home_points} mid={hm} isHome />
                <BreakdownSide name={m.away_name} pts={m.away_points} mid={am} />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-slate-200 p-3 text-center">
                  <div className="font-bold text-sm">{m.home_name}</div>
                  <div className="display font-black text-lg mt-1">{m.home_points} pt</div>
                </div>
                <div className="rounded-xl border border-slate-200 p-3 text-center">
                  <div className="font-bold text-sm">{m.away_name}</div>
                  <div className="display font-black text-lg mt-1">{m.away_points} pt</div>
                </div>
              </div>
            )}
            {hasCup && (
              <div className="mt-4 text-xs text-slate-500 border-t border-slate-100 pt-3">
                <div className="font-semibold text-slate-700 mb-1">Come è stato ricalcolato:</div>
                Il punteggio Coppa parte dal totale Campionato, <b>rimuove</b> il modificatore centrocampo di Campionato
                (che potrebbe essere stato calcolato contro un altro avversario di campionato) e applica il modificatore
                specifico Coppa in base al diff dei voti base dei centrocampisti titolari tra le due squadre di Coppa.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function BreakdownSide({ name, pts, mid, isHome }) {
  const finalMid = mid.cup_mod - mid.league_mod;
  return (
    <div className={`rounded-xl border-2 p-3 text-center ${isHome ? "border-blue-200 bg-blue-50" : "border-purple-200 bg-purple-50"}`}>
      <div className="font-bold text-sm truncate">{name}</div>
      <div className="display font-black text-lg mt-1">{pts} pt</div>
      <div className="mt-2 space-y-1 text-[10px] text-left">
        <div className="flex justify-between">
          <span className="text-slate-600">Base voto CEN:</span>
          <b>{mid.base_sum?.toFixed(2) ?? "-"}</b>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-600">Mod. Campionato:</span>
          <span className="text-slate-500">-{mid.league_mod?.toFixed(2) ?? "0"}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-600">Mod. Coppa:</span>
          <span className={mid.cup_mod > 0 ? "text-emerald-600 font-bold" : "text-slate-500"}>
            +{mid.cup_mod?.toFixed(2) ?? "0"}
          </span>
        </div>
        <div className="flex justify-between border-t border-slate-200 pt-1">
          <span className="text-slate-700 font-semibold">Delta:</span>
          <b className={finalMid > 0 ? "text-emerald-600" : finalMid < 0 ? "text-red-600" : ""}>
            {finalMid >= 0 ? "+" : ""}{finalMid.toFixed(2)}
          </b>
        </div>
      </div>
    </div>
  );
}

function MaxiStandings({ rows }) {
  const navigate = useNavigate();
  const zoneOf = (rank) =>
    rank <= 8 ? "green" :
    rank <= 24 ? "yellow" :
    "orange";
  const zoneBg = {
    green:  "rgba(16,185,129,0.10)",   // emerald-500 @ 10%
    yellow: "rgba(234,179,8,0.10)",    // yellow-500 @ 10%
    orange: "rgba(249,115,22,0.12)",   // orange-500 @ 12%
  };
  const zoneHover = {
    green:  "rgba(16,185,129,0.18)",
    yellow: "rgba(234,179,8,0.18)",
    orange: "rgba(249,115,22,0.22)",
  };
  return (
    <div className="card-surface rounded-2xl overflow-hidden" data-testid="maxi-standings">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Classifica Maxi Girone</div>
          <div className="display font-black text-lg">32 squadre · 8 giornate</div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-widest">
            <tr>
              <th className="px-3 py-3 text-left w-12">POS</th>
              <th className="px-3 py-3 text-left">Squadra</th>
              <th className="px-3 py-3 text-center hidden lg:table-cell w-16">Girone</th>
              <th className="px-3 py-3 text-center w-12">PG</th>
              <th className="px-2 py-3 text-center w-10 hidden sm:table-cell">V</th>
              <th className="px-2 py-3 text-center w-10 hidden sm:table-cell">N</th>
              <th className="px-2 py-3 text-center w-10 hidden sm:table-cell">P</th>
              <th className="px-2 py-3 text-center w-14 hidden md:table-cell">GF</th>
              <th className="px-2 py-3 text-center w-14 hidden md:table-cell">GS</th>
              <th className="px-2 py-3 text-center w-14 hidden md:table-cell">DR</th>
              <th className="px-3 py-3 text-right w-16">PT</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => {
              const z = zoneOf(t.rank);
              const go = () => t.team_id && navigate(`/squadra/${t.team_id}`);
              return (
                <tr key={t.team_id}
                    role="button"
                    tabIndex={0}
                    onClick={go}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); go(); } }}
                    className="border-t border-slate-100 transition-colors cursor-pointer focus:outline-none"
                    style={{ background: zoneBg[z] }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = zoneHover[z]; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = zoneBg[z]; }}
                    data-testid={`maxi-row-${t.team_id}`}
                    data-zone={z}>
                  <td className="px-3 py-3 font-black text-slate-700">{t.rank}</td>
                  <td className="px-3 py-3">
                    <Link to={`/squadra/${t.team_id}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-3 hover:text-slate-900">
                      <img src={t.shield_path ? publicFileUrl(t.shield_path) : SHIELD_PLACEHOLDER}
                           onError={(e) => { e.currentTarget.src = SHIELD_PLACEHOLDER; }}
                           alt="" className="w-8 h-8 rounded-md object-cover shrink-0" />
                      <span className="font-semibold text-slate-900 truncate">{t.team_name}</span>
                    </Link>
                  </td>
                  <td className="px-3 py-3 text-center hidden lg:table-cell">
                    <div className="inline-flex justify-center"><GironeBadge girone={t.girone} size={22} /></div>
                  </td>
                  <td className="px-3 py-3 text-center text-slate-700 font-bold tabular-nums">{t.played}</td>
                  <td className="px-2 py-3 text-center hidden sm:table-cell text-emerald-600 font-bold tabular-nums">{t.wins}</td>
                  <td className="px-2 py-3 text-center hidden sm:table-cell text-slate-500 font-bold tabular-nums">{t.draws}</td>
                  <td className="px-2 py-3 text-center hidden sm:table-cell text-rose-500 font-bold tabular-nums">{t.losses}</td>
                  <td className="px-2 py-3 text-center hidden md:table-cell text-slate-700 tabular-nums">{t.gf}</td>
                  <td className="px-2 py-3 text-center hidden md:table-cell text-slate-700 tabular-nums">{t.gs}</td>
                  <td className={`px-2 py-3 text-center hidden md:table-cell font-bold tabular-nums ${t.dr > 0 ? "text-emerald-600" : t.dr < 0 ? "text-rose-500" : "text-slate-500"}`}>
                    {t.dr > 0 ? "+" : ""}{t.dr}
                  </td>
                  <td className="px-3 py-3 text-right font-black tabular-nums" style={{ color: "var(--brand)" }}>{t.points}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Legenda zone qualificazione */}
      <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/40 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] text-slate-700">
        <span className="inline-flex items-center gap-2" data-testid="legend-green">
          <span className="w-3 h-3 rounded" style={{ background: "rgba(16,185,129,0.55)" }} />
          <span>Qualificate agli <b>Ottavi di Champions League</b></span>
        </span>
        <span className="inline-flex items-center gap-2" data-testid="legend-yellow">
          <span className="w-3 h-3 rounded" style={{ background: "rgba(234,179,8,0.65)" }} />
          <span>Qualificate per i <b>Playoff di Champions League</b></span>
        </span>
        <span className="inline-flex items-center gap-2" data-testid="legend-orange">
          <span className="w-3 h-3 rounded" style={{ background: "rgba(249,115,22,0.7)" }} />
          <span>Agli <b>Ottavi di Europa League</b></span>
        </span>
      </div>
    </div>
  );
}

function MaxiRounds({ rounds }) {
  const defRound = String(rounds[0]?.round || 1);
  return (
    <div className="card-surface rounded-2xl p-5" data-testid="maxi-rounds">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-3">Calendario · 8 giornate</div>
      <Tabs defaultValue={defRound}>
        <TabsList className="bg-slate-50 border border-slate-200 flex-wrap h-auto">
          {rounds.map((r) => (
            <TabsTrigger key={r.round} value={String(r.round)} className="data-[state=active]:bg-[var(--brand)] data-[state=active]:text-white" data-testid={`maxi-round-tab-${r.round}`}>
              G{r.round}
            </TabsTrigger>
          ))}
        </TabsList>
        {rounds.map((r) => (
          <TabsContent key={r.round} value={String(r.round)} className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {r.matches.map((m) => <MaxiMatchRow key={m.id} m={m} />)}
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function MaxiMatchRow({ m }) {
  const played = m.status === "played";
  const hs = played ? m.home_score : null;
  const as_ = played ? m.away_score : null;
  const homeWin = played && hs > as_;
  const awayWin = played && as_ > hs;
  return (
    <div className="card-surface rounded-xl px-4 py-3 flex items-center gap-3" data-testid={`maxi-match-${m.id}`}>
      <div className="flex-1 flex items-center gap-2 min-w-0">
        <GironeBadge girone={m.home_girone} size={20} />
        <span className={`text-sm truncate ${homeWin ? "font-black text-slate-900" : "text-slate-600"}`}>{m.home_name}</span>
      </div>
      <div className="shrink-0 display font-black text-lg tabular-nums">
        {played ? (
          <>
            <span className={homeWin ? "text-slate-900" : "text-slate-400"}>{hs}</span>
            <span className="text-slate-300 mx-1">–</span>
            <span className={awayWin ? "text-slate-900" : "text-slate-400"}>{as_}</span>
          </>
        ) : (
          <span className="text-slate-400 text-xs uppercase tracking-widest">VS</span>
        )}
      </div>
      <div className="flex-1 flex items-center gap-2 justify-end min-w-0">
        <span className={`text-sm truncate text-right ${awayWin ? "font-black text-slate-900" : "text-slate-600"}`}>{m.away_name}</span>
        <GironeBadge girone={m.away_girone} size={20} />
      </div>
    </div>
  );
}

function BracketTeam({ team, size = 32, animate = false, delay = 0 }) {
  if (!team) return <div className="text-slate-400 text-xs">TBD</div>;
  return (
    <div className={`flex items-center gap-2 ${animate ? "fade-up" : ""}`} style={{ animationDelay: `${delay}ms` }}>
      <img src={team.shield_path ? publicFileUrl(team.shield_path) : SHIELD_PLACEHOLDER}
           onError={(e) => { e.currentTarget.src = SHIELD_PLACEHOLDER; }}
           alt="" style={{ width: size, height: size }} className="rounded-md object-cover shrink-0" />
      <span className="text-sm truncate">{team.team_name}</span>
    </div>
  );
}

function MatchRow({ m, delay = 0, animate = false }) {
  return (
    <div className="card-surface rounded-xl p-3 space-y-2 min-w-[220px]">
      <div className="flex justify-between items-center">
        <BracketTeam team={m?.a} animate={animate} delay={delay} />
        <span className="display font-black text-slate-600">{m?.score_a ?? "-"}</span>
      </div>
      <div className="flex justify-between items-center">
        <BracketTeam team={m?.b} animate={animate} delay={delay + 100} />
        <span className="display font-black text-slate-600">{m?.score_b ?? "-"}</span>
      </div>
    </div>
  );
}

function BracketView({ competition, accent, icon: Icon, title }) {
  const [bracket, setBracket] = useState(null);
  const [animKey, setAnimKey] = useState(0);

  const load = async () => {
    try {
      const { data } = await api.get(`/public/coppa/${competition}`);
      setBracket(data);
    } catch {}
  };

  useEffect(() => { load(); }, [competition]);

  const hasBracket = bracket && (bracket.quarterfinals || []).length > 0;

  return (
    <CoppaShell title={title} icon={Icon} accent={accent}>
      {!hasBracket ? (
        <div className="card-surface rounded-3xl p-10 text-center">
          <Shuffle className="w-8 h-8 mx-auto" style={{ color: accent }} />
          <div className="display font-bold text-2xl mt-3">Tabellone non ancora sorteggiato</div>
          <p className="text-slate-500 text-sm mt-2 max-w-lg mx-auto">
            L'Admin genererà il tabellone dopo la fase a gironi. Torna qui per la cerimonia di sorteggio.
          </p>
        </div>
      ) : (
        <div key={animKey} className="space-y-4">
          <div className="flex items-center gap-3">
            <button onClick={() => setAnimKey((k) => k + 1)}
                    className="glass px-3 py-1.5 rounded-full text-xs hover:bg-slate-100"
                    data-testid={`replay-draw-${competition}`}>
              Rigioca sorteggio
            </button>
            <div className="text-slate-500 text-xs">Sorteggiato il {bracket.created_at?.slice(0, 10)}</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 overflow-x-auto">
            <div className="space-y-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Quarti di finale</div>
              {bracket.quarterfinals.map((m, i) => (
                <MatchRow key={i} m={m} animate delay={i * 150} />
              ))}
            </div>
            <div className="space-y-6 flex flex-col justify-center">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Semifinali</div>
              {bracket.semifinals.map((m, i) => (
                <MatchRow key={i} m={m} animate delay={800 + i * 200} />
              ))}
            </div>
            <div className="flex flex-col justify-center gap-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Finale</div>
              <MatchRow m={bracket.final} animate delay={1400} />
              {bracket.champion && (
                <div className="rounded-2xl p-4 fade-up text-center"
                     style={{ background: `${accent}22`, border: `1px solid ${accent}55`, animationDelay: "1700ms" }}>
                  <Trophy className="w-6 h-6 mx-auto" style={{ color: accent }} />
                  <div className="display font-black text-lg mt-1" style={{ color: accent }}>{bracket.champion.team_name}</div>
                  <div className="text-slate-500 text-xs">Campione FIFE {competition === "champions" ? "Champions" : "Europa"} 2026/27</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </CoppaShell>
  );
}

/* ============ OTTAVI Europa League ============ */
export function CoppaOttaviEuropa() {
  const [matches, setMatches] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api.get("/public/coppa/europa-ottavi/matches")
      .then(({ data }) => setMatches(data || []))
      .catch(() => setMatches([]))
      .finally(() => setLoaded(true));
  }, []);
  return (
    <CoppaShell title="Ottavi Europa League" icon={Rocket} accent="#f97316">
      <div className="card-surface rounded-2xl p-5 md:p-6" data-testid="europa-ottavi-formato">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">Formato</div>
        <p className="text-slate-700 text-sm md:text-base mt-3 leading-relaxed">
          16 squadre in sfida secca a eliminazione diretta: le <b>8 teste di serie</b> (i perdenti dei Playoff Champions)
          affrontano le squadre dalla <b>25ª alla 32ª</b> del maxi girone. Abbinamenti standard <b>1 vs 16</b>, <b>2 vs 15</b>, …, <b>8 vs 9</b>.
        </p>
      </div>
      {loaded && matches.length === 0 && (
        <div className="card-surface rounded-3xl p-10 text-center">
          <Rocket className="w-8 h-8 mx-auto" style={{ color: "#f97316" }} />
          <div className="display font-bold text-2xl mt-3">Ottavi EL non ancora sorteggiati</div>
          <p className="text-slate-500 text-sm mt-2 max-w-lg mx-auto">
            L'Admin li genererà dopo il completamento dei Playoff Champions.
          </p>
        </div>
      )}
      {matches.length > 0 && (
        <div className="card-surface rounded-2xl p-5" data-testid="europa-ottavi-bracket">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-4">Tabellone Ottavi EL · 8 sfide</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {matches.map((m) => <PlayoffMatchRow key={m.id} m={m} />)}
          </div>
        </div>
      )}
    </CoppaShell>
  );
}

export function CoppaChampions() {
  const [ottavi, setOttavi] = useState([]);
  useEffect(() => {
    api.get("/public/coppa/ottavi/matches").then(({ data }) => setOttavi(data || [])).catch(() => setOttavi([]));
  }, []);
  return (
    <CoppaShell title="Champions League" icon={Trophy} accent="#60a5fa">
      <BracketOverview ottavi={ottavi} accent="#60a5fa" competition="Champions" />
      <BracketStage title="Ottavi di Finale" matches={ottavi} accent="#60a5fa" empty="Ottavi non ancora sorteggiati (Admin)." />
      <CupStagePlaceholder title="Quarti di Finale" />
      <CupStagePlaceholder title="Semifinali" />
      <CupStagePlaceholder title="Finalissima Champions" />
    </CoppaShell>
  );
}

export function CoppaEuropa() {
  const [ottavi, setOttavi] = useState([]);
  useEffect(() => {
    api.get("/public/coppa/europa-ottavi/matches").then(({ data }) => setOttavi(data || [])).catch(() => setOttavi([]));
  }, []);
  return (
    <CoppaShell title="Europa League" icon={Rocket} accent="#f97316">
      <BracketOverview ottavi={ottavi} accent="#f97316" competition="Europa" />
      <BracketStage title="Ottavi di Finale" matches={ottavi} accent="#f97316" empty="Ottavi EL non ancora sorteggiati (Admin)." />
      <CupStagePlaceholder title="Quarti di Finale" />
      <CupStagePlaceholder title="Semifinali" />
      <CupStagePlaceholder title="Finalissima Europa League" />
    </CoppaShell>
  );
}

function BracketStage({ title, matches, accent, empty }) {
  return (
    <div className="card-surface rounded-2xl p-5" data-testid={`bracket-stage-${title.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Fase</div>
          <div className="display font-black text-lg">{title}</div>
        </div>
        <span className="text-xs text-slate-500 font-bold uppercase tracking-widest">{matches.length}/8 · andata + ritorno</span>
      </div>
      {matches.length === 0 ? (
        <div className="text-slate-500 text-sm italic px-2 py-6 text-center">{empty}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {matches.map((m) => <TwoLegMatchRow key={m.id} m={m} accent={accent} />)}
        </div>
      )}
    </div>
  );
}

/* ============ OTTAVI Champions ============ */
export function CoppaOttaviChampions() {
  const [matches, setMatches] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api.get("/public/coppa/ottavi/matches")
      .then(({ data }) => setMatches(data || []))
      .catch(() => setMatches([]))
      .finally(() => setLoaded(true));
  }, []);
  return (
    <CoppaShell title="Ottavi Champions" icon={Trophy} accent="#60a5fa">
      <div className="card-surface rounded-2xl p-5 md:p-6" data-testid="ottavi-formato">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">Formato</div>
        <p className="text-slate-700 text-sm md:text-base mt-3 leading-relaxed">
          16 squadre in sfida secca a eliminazione diretta: le <b>8 teste di serie</b> (top-8 del maxi girone) affrontano
          le <b>8 vincenti dei Playoff</b>. Abbinamenti CL standard <b>1 vs 16</b>, <b>2 vs 15</b>, …, <b>8 vs 9</b>.
        </p>
      </div>
      {loaded && matches.length === 0 && (
        <div className="card-surface rounded-3xl p-10 text-center">
          <Trophy className="w-8 h-8 mx-auto" style={{ color: "#60a5fa" }} />
          <div className="display font-bold text-2xl mt-3">Ottavi non ancora sorteggiati</div>
          <p className="text-slate-500 text-sm mt-2 max-w-lg mx-auto">
            L'Admin lo genererà dopo il completamento dei Playoff.
          </p>
        </div>
      )}
      {matches.length > 0 && (
        <div className="card-surface rounded-2xl p-5" data-testid="ottavi-bracket">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-4">Tabellone Ottavi · 8 sfide</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {matches.map((m) => <PlayoffMatchRow key={m.id} m={m} />)}
          </div>
        </div>
      )}
    </CoppaShell>
  );
}

/* ============ PLAYOFF Champions (16 squadre — 9ª vs 24ª ecc.) ============ */
export function CoppaPlayoff() {
  const [matches, setMatches] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api.get("/public/coppa/playoff/matches")
      .then(({ data }) => setMatches(data || []))
      .catch(() => setMatches([]))
      .finally(() => setLoaded(true));
  }, []);
  return (
    <CoppaShell title="Playoff Champions" icon={Shuffle} accent="#f59e0b">
      <div className="card-surface rounded-2xl p-5 md:p-6" data-testid="playoff-formato">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">Formato</div>
        <p className="text-slate-700 text-sm md:text-base mt-3 leading-relaxed">
          Doppia sfida (andata + ritorno) fra le squadre dalla <b>9ª alla 24ª</b> del maxi girone: <b>9ª vs 24ª</b>, <b>10ª vs 23ª</b>,
          <b> 11ª vs 22ª</b>, <b>12ª vs 21ª</b>, <b>13ª vs 20ª</b>, <b>14ª vs 19ª</b>, <b>15ª vs 18ª</b>, <b>16ª vs 17ª</b>.
          Le <b>8 vincenti</b> volano agli <b>Ottavi di Champions</b>, le <b>8 perdenti</b> agli <b>Ottavi di Europa League</b>.
        </p>
      </div>
      {loaded && matches.length === 0 && (
        <div className="card-surface rounded-3xl p-10 text-center">
          <Shuffle className="w-8 h-8 mx-auto" style={{ color: "#f59e0b" }} />
          <div className="display font-bold text-2xl mt-3">Playoff non ancora sorteggiati</div>
          <p className="text-slate-500 text-sm mt-2 max-w-lg mx-auto">
            Il tabellone si genera automaticamente dalla classifica Maxi Girone. L'Admin lo lancia dal pannello Coppa Maxi.
          </p>
        </div>
      )}
      {matches.length > 0 && (
        <div className="card-surface rounded-2xl p-5" data-testid="playoff-bracket">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-4">Tabellone Playoff · 8 doppie sfide (andata + ritorno)</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {matches.map((m) => <TwoLegMatchRow key={m.id} m={m} accent="#f59e0b" />)}
          </div>
        </div>
      )}
    </CoppaShell>
  );
}

function PlayoffMatchRow({ m }) {
  const played = m.status === "played";
  const homeWin = played && m.winner_id === m.home_id;
  const awayWin = played && m.winner_id === m.away_id;
  return (
    <div className="border border-slate-200 rounded-xl p-3 flex items-center gap-3 hover:border-[var(--brand)]/40 transition"
         data-testid={`playoff-match-${m.id}`}>
      <div className="flex-1 flex items-center gap-2 min-w-0">
        <span className="text-[10px] font-black px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 shrink-0">#{m.seed_home}</span>
        <GironeBadge girone={m.home_girone} size={18} />
        <span className={`text-sm truncate ${homeWin ? "font-black text-slate-900" : "text-slate-600"}`}>{m.home_name}</span>
        {homeWin && <Trophy className="w-3.5 h-3.5 shrink-0" style={{ color: "#f59e0b" }} />}
      </div>
      <div className="shrink-0 display font-black text-lg tabular-nums">
        {played ? (
          <>
            <span className={homeWin ? "text-slate-900" : "text-slate-400"}>{m.home_score}</span>
            <span className="text-slate-300 mx-1">–</span>
            <span className={awayWin ? "text-slate-900" : "text-slate-400"}>{m.away_score}</span>
          </>
        ) : (
          <span className="text-slate-400 text-xs uppercase tracking-widest">VS</span>
        )}
      </div>
      <div className="flex-1 flex items-center gap-2 justify-end min-w-0">
        {awayWin && <Trophy className="w-3.5 h-3.5 shrink-0" style={{ color: "#f59e0b" }} />}
        <span className={`text-sm truncate text-right ${awayWin ? "font-black text-slate-900" : "text-slate-600"}`}>{m.away_name}</span>
        <GironeBadge girone={m.away_girone} size={18} />
        <span className="text-[10px] font-black px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 shrink-0">#{m.seed_away}</span>
      </div>
    </div>
  );
}
